import React from "react";
import { Story } from "../../../types";
import { constants } from "../../../constants";
import { SafeJoin } from "../../../util";
import Button from "@/components/ui/button";
import FakeButton from "@/components/fake-button";
import ActiveFakeButton from "@/components/active-fake-button";

type Props = {
  onClick?: () => void;
  story: Story;
  disabled?: boolean;
  hideAuthors?: boolean;
  showReactions?: boolean;
  showAwards?: boolean;
  size?: "base" | "lg";
};

const StoryButton = ({
  onClick,
  story,
  disabled,
  hideAuthors,
  showReactions,
  showAwards,
  size = "lg",
}: Props) => {
  const emojis = constants.emojis;
  function handleClick() {
    if (onClick) {
      onClick();
    }
  }

  function reactions(story: Story) {
    let entries = Object.entries(story.meta.reactions).sort(
      (a, b) => b[1] - a[1]
    );
    return entries.map<React.ReactNode>((value) => (
      <span key={"emoji-" + value}>
        {emojis[parseInt(value[0])]}
        <sub>{value[1]}</sub>
      </span>
    ));
  }

  function awards(story: Story) {
    return story.meta.awards.map<React.ReactNode>((s) => (
      <span key={"trophy-" + s}>{constants.trophy + s + constants.trophy}</span>
    ));
  }

  function storyTitle(story: Story) {
    return (
      <div className={"break-words p-0.5 w-full max-w-full"}>
        <div className={"text-xl underline font-bold break-words max-w-full"}>
          {story.meta.title}
        </div>
        <div>
          CHARACTERS:{" "}
          {SafeJoin(
            story.characters
              ?.filter((c) => c.selected)
              .map<React.ReactNode>((c, a, b) => (
                <span className={"italic break-words"} key={"char-" + c.id}>
                  {c.name}
                </span>
              )),
            ", "
          )}
        </div>

        {!hideAuthors && (
          <div>
            WRITERS:{" "}
            {SafeJoin(
              story.submissions?.map<React.ReactNode>((s) => (
                <span
                  className={"italic"}
                  key={"player-" + s.player_id + "-" + s.id}
                >
                  {s.player.name}
                </span>
              )),
              ", "
            )}
          </div>
        )}

        <div>{story.settings.mature ? "rated 18+" : "rated all ages"}</div>
      </div>
    );
  }

  function storyReactionsAndAwards(story: Story) {
    return (
      <>
        {showAwards && story.meta.awards && (
          <>
            <span className={"italic font-bold"}>AWARD WINNER:</span>
            {awards(story)}
            <br />
          </>
        )}
        {showReactions && story.meta.reactions && (
          <span>
            <b style={{ fontStyle: "italic" }}>REACTS:</b>{" "}
            {reactions(story)}{" "}
          </span>
        )}
      </>
    );
  }

  if (showReactions || showAwards) {
    return (
      <div className={"flex-grow flex"}>
        <div className={"flex-grow flex flex-col"}>
          {onClick ? (
            <Button
              type="button"
              className={`break-words p-4 flex-grow ${size === "base" ? "text-base" : "text-lg"}`}
              disabled={disabled}
              onClick={handleClick}
            >
              {storyTitle(story)}
            </Button>
          ) : (
            <ActiveFakeButton
              className={`break-words p-4 flex-grow ${size === "base" ? "text-base" : "text-lg"}`}
            >
              {storyTitle(story)}
            </ActiveFakeButton>
          )}

          <div
            className={
              "text-center p-4 flex justify-center flex-col items-center"
            }
          >
            {storyReactionsAndAwards(story)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={"grow min-w-0"}>
      <div className={"cursor-pointer"}>
        {onClick ? (
          <Button
            type="button"
            className={`w-full p-0 ${size === "base" ? "text-base" : "text-lg leading-7"} grow`}
            disabled={disabled}
            onClick={handleClick}
          >
            {storyTitle(story)}
          </Button>
        ) : (
          <ActiveFakeButton
            className={`w-full p-0 ${size === "base" ? "text-base" : "text-lg leading-7"} grow`}
          >
            {storyTitle(story)}
          </ActiveFakeButton>
        )}
      </div>
    </div>
  );
};

export default StoryButton;
